import { Suspense, lazy } from 'react';
import { Navigate, Outlet } from 'react-router-dom';
// auth
import { AuthGuard, PermissionGuard } from 'src/auth/guard';
// layouts
import DashboardLayout from 'src/layouts/dashboard';
// components
import { LoadingScreen } from 'src/components/loading-screen';


// ----------------------------------------------------------------------

// OVERVIEW
const AppPage = lazy(() => import('src/pages/dashboard/app'));
// USER MANAGEMENT
const UserListPage = lazy(() => import('src/pages/dashboard/user/list'));
const UserCreatePage = lazy(() => import('src/pages/dashboard/user/new'));
const UserEditPage = lazy(() => import('src/pages/dashboard/user/edit'));
const UserAccount = lazy(() => import('src/pages/dashboard/user/account'));
const UserAttendancePage = lazy(() => import('src/pages/dashboard/user/attendance'));
const UserPaymentsPage = lazy(() => import('src/pages/dashboard/user/payments'));
const UserIncentivesPage = lazy(() => import('src/pages/dashboard/user/incentives'));
const UserExpensesTravelPage = lazy(() => import('src/pages/dashboard/user/expenses-travel'));
const UserWalletPage = lazy(() => import('src/pages/dashboard/user/wallet'));
// ROLE MANAGEMENT
const RoleListPage = lazy(() => import('src/pages/dashboard/role/list'));
const RoleCreatePage = lazy(() => import('src/pages/dashboard/role/new'));
const RoleEditPage = lazy(() => import('src/pages/dashboard/role/edit'));
// JOB MANAGEMENT
const JobListPage = lazy(() => import('src/pages/dashboard/job/list'));
const JobCreatePage = lazy(() => import('src/pages/dashboard/job/new'));
const JobEditPage = lazy(() => import('src/pages/dashboard/job/edit'));
const JobDetailsPage = lazy(() => import('src/pages/dashboard/job/details'));
// PROPOSALS
const ProposalsPage = lazy(() => import('src/pages/dashboard/job/proposals'));
const ProposalDetailsPage = lazy(() => import('src/pages/dashboard/job/proposal-details'));
// LOCATION
const LocationListPage = lazy(() => import('src/pages/dashboard/location/list'));
const LocationCreatePage = lazy(() => import('src/pages/dashboard/location/create'));
const LocationDetailsPage = lazy(() => import('src/pages/dashboard/location/details'));
// EQUIPMENT
const EquipmentListPage = lazy(() => import('src/pages/dashboard/equipment/list'));
const EquipmentAddPage = lazy(() => import('src/pages/dashboard/equipment/add'));
const EquipmentDetailsPage = lazy(() => import('src/pages/dashboard/equipment/details'));
// APPLICATION MANAGEMENT
const JobAppliedPage = lazy(() => import('src/pages/dashboard/application/applied'));
const JobReceivedPage = lazy(() => import('src/pages/dashboard/application/received'));
const ApplicationDetailsView = lazy(() => import('src/sections/application/view/application-details-view'));
const AppliedApplicationDetailsView = lazy(() => import('src/sections/application/view/applied-application-details-view'));
// TALENT MANAGEMENT
const TalentListPage = lazy(() => import('src/pages/dashboard/talent/list'));
const TalentHirePage = lazy(() => import('src/pages/dashboard/talent/hired'));
const TalentAvailabilityPage = lazy(() => import('src/pages/dashboard/talent/availability'));
const TalentOffersPage = lazy(() => import('src/pages/dashboard/talent/offers'));
const TalentOfferNewPage = lazy(() => import('src/pages/dashboard/talent/offer-new'));
const TalentBookingsPage = lazy(() => import('src/pages/dashboard/talent/bookings'));
const TalentBookingDetailsPage = lazy(() => import('src/pages/dashboard/talent/booking-details'));
const TalentBookingEditPage = lazy(() => import('src/pages/dashboard/talent/booking-edit'));
const TalentCreatePage = lazy(() => import('src/pages/dashboard/talent/new'));
const TalentEditPage = lazy(() => import('src/pages/dashboard/talent/edit'));
const TalentDetailsPage = lazy(() => import('src/pages/dashboard/talent/details'));
const TalentProfilePage = lazy(() => import('src/pages/dashboard/talent/profile'));
const TalentToursPage = lazy(() => import('src/pages/dashboard/talent/tours'));
const TalentTravelStaysPage = lazy(() => import('src/pages/dashboard/talent/travel-stays'));
const TalentCompliancesPage = lazy(() => import('src/pages/dashboard/talent/compliances'));
// CHAT
const ChatPage = lazy(() => import('src/pages/dashboard/chat'));
// CLIENT MANAGEMENT
const ClientListPage = lazy(() => import('src/pages/dashboard/client/list'));
const ClientCreatePage = lazy(() => import('src/pages/dashboard/client/new'));
const ClientEditPage = lazy(() => import('src/pages/dashboard/client/edit'));
const ClientDetailsPage = lazy(() => import('src/pages/dashboard/client/details'));
// CALENDAR
const CalendarPage = lazy(() => import('src/pages/dashboard/calendar'));
// INVOICE
const InvoiceListPage = lazy(() => import('src/pages/dashboard/invoice/list'));
const InvoiceDetailsPage = lazy(() => import('src/pages/dashboard/invoice/details'));
const AgentChatPage = lazy(() => import('src/pages/dashboard/agent'));
const InvoiceCreatePage = lazy(() => import('src/pages/dashboard/invoice/new'));
const InvoiceEditPage = lazy(() => import('src/pages/dashboard/invoice/edit'));
const PerformaInvoicePage = lazy(() => import('src/pages/dashboard/invoice/performa'));
const OrdersListPage = lazy(() => import('src/pages/dashboard/orders/list'));
const OrdersDetailsPage = lazy(() => import('src/pages/dashboard/orders/details'));
// EXPENSE CLAIMS REVIEW
const ExpenseClaimsReviewPage = lazy(() => import('src/pages/dashboard/expenses/review'));
// PROJECT MANAGEMENT
const ProjectListPage = lazy(() => import('src/pages/dashboard/projects/list'));
const ProjectCreatePage = lazy(() => import('src/pages/dashboard/projects/new'));
const ProjectEditPage = lazy(() => import('src/pages/dashboard/projects/edit'));
const ProjectDetailsPage = lazy(() => import('src/pages/dashboard/projects/details'));
const ProjectKanbanPage = lazy(() => import('src/pages/dashboard/projects/kanban'));
const ProjectCalendarPage = lazy(() => import('src/pages/dashboard/projects/calendar'));
const ProjectAnalyticsPage = lazy(() => import('src/pages/dashboard/projects/analytics'));
const ProjectAllTasksPage = lazy(() => import('src/pages/dashboard/projects/all-tasks'));
const ProjectAttendencePage = lazy(() => import('src/pages/dashboard/projects/attendence'));
const ProjectListPayrollPage = lazy(() => import('src/pages/dashboard/projects/list-payroll'));
const ProjectPayoutQueuePage = lazy(() => import('src/pages/dashboard/projects/payout-queue'));
const ProjectFreelancerPage = lazy(() => import('src/pages/dashboard/projects/freelancer'));
const ProjectIncentivesPage = lazy(() => import('src/pages/dashboard/projects/incentives'));
const ProjectTemplatesPage = lazy(() => import('src/pages/dashboard/projects/templates'));
const ProjectTemplateNewPage = lazy(() => import('src/pages/dashboard/projects/template-new'));
const ProjectTemplateEditPage = lazy(() => import('src/pages/dashboard/projects/template-edit'));
// REPORTS
const ClientRevenueReportPage = lazy(() => import('src/pages/dashboard/reports/client-revenue'));
const TalentEarningsReportPage = lazy(() => import('src/pages/dashboard/reports/talent-earnings'));
const CompanyEarningsReportPage = lazy(() => import('src/pages/dashboard/reports/company-earnings'));
const ExpenseReportPage = lazy(() => import('src/pages/dashboard/reports/expense-report'));
const ProjectReportsPage = lazy(() => import('src/pages/dashboard/reports/project-reports'));
const SettlementsPage = lazy(() => import('src/pages/dashboard/reports/settlements'));
// CONTRACTS
const ContractsListPage = lazy(() => import('src/pages/dashboard/contracts/list'));
const ContractDetailPage = lazy(() => import('src/pages/dashboard/contracts/detail'));
const ContractsTemplatesPage = lazy(() => import('src/pages/dashboard/contracts/templates'));
const ContractTemplateNewPage = lazy(() => import('src/pages/dashboard/contracts/template-new'));
const ContractTemplateEditPage = lazy(() => import('src/pages/dashboard/contracts/template-edit'));
const ContractSignDashboardPage = lazy(() => import('src/pages/dashboard/contracts/sign'));
// AUDITIONS
const AuditionsListPage = lazy(() => import('src/pages/dashboard/auditions/list'));
const AuditionNewPage = lazy(() => import('src/pages/dashboard/auditions/new'));
const AuditionEditPage = lazy(() => import('src/pages/dashboard/auditions/edit'));
const AuditionDetailPage = lazy(() => import('src/pages/dashboard/auditions/detail'));
// BOOKINGS
const BookingsListPage = lazy(() => import('src/pages/dashboard/bookings/list'));
// PARTNERS
const PartnersListPage = lazy(() => import('src/pages/dashboard/partners/list'));
// NOTIFICATIONS
const NotificationsPage = lazy(() => import('src/pages/dashboard/notifications'));
const NotificationDetailPage = lazy(() => import('src/pages/dashboard/notification-detail'));
// FILM MODULE
const FilmListPage = lazy(() => import('src/pages/dashboard/film/list'));
const FilmWorkspacePage = lazy(() => import('src/pages/dashboard/film/workspace'));
const FilmScriptEditorPage = lazy(() => import('src/pages/dashboard/film/script-editor'));
// DOCUMENTS
const DocumentsPage = lazy(() => import('src/pages/dashboard/documents'));
const AcceptSharePage = lazy(() => import('src/pages/dashboard/documents/accept-share'));
const AcceptFileSharePage = lazy(() => import('src/pages/dashboard/documents/accept-file-share'));
// ----------------------------------------------------------------------

export const dashboardRoutes = [
  {
    path: '/dashboard',
    element: (
      <AuthGuard>
        <DashboardLayout>
          <Suspense fallback={<LoadingScreen />}>
            <Outlet />
          </Suspense>
        </DashboardLayout>
      </AuthGuard>
    ),
    children: [
      { element: <AppPage />, index: true },
      { path: 'summary',
        children: [
          { element: <AppPage />, index: true },
          { path: 'app', element: <AppPage /> },
        ]
      },
      // USER MANAGEMENT - Protected with PermissionGuard
      // NOTE: You don't have "user_management" in your permissions table yet
      // Either add it to the database OR remove PermissionGuard from these routes
      {
        path: 'user',
        children: [
          {
            element: (
              <PermissionGuard permission="user_management" accessType="read">
                <UserListPage permissions={{check:true}}/>
              </PermissionGuard>
            ),
            index: true
          },
          {
            path:'new',
            element: (
              <PermissionGuard permission="user_management" accessType="write">
                <UserCreatePage />
              </PermissionGuard>
            )
          },
          {
            path: ':id/edit',
            element: (
              <PermissionGuard permission="user_management" accessType="write">
                <UserEditPage />
              </PermissionGuard>
            )
          },
          { path: 'account', element: <UserAccount /> },
          { path: 'attendance', element: <UserAttendancePage /> },
          { path: 'payments', element: <UserPaymentsPage /> },
          { path: 'incentives', element: <UserIncentivesPage /> },
          { path: 'expenses-travel', element: <UserExpensesTravelPage /> },
          { path: 'wallet', element: <UserWalletPage /> },
        ]
      },
      // ROLE MANAGEMENT - Protected with PermissionGuard
      // NOTE: You don't have "role_management" in your permissions table yet
      {
        path: 'role',
        children: [
          {
            element: (
              <PermissionGuard permission="role_management" accessType="read">
                <RoleListPage permissions={{check:true}}/>
              </PermissionGuard>
            ),
            index: true
          },
          {
            path:'new',
            element: (
              <PermissionGuard permission="role_management" accessType="write">
                <RoleCreatePage />
              </PermissionGuard>
            )
          },
          {
            path: ':id/edit',
            element: (
              <PermissionGuard permission="role_management" accessType="write">
                <RoleEditPage />
              </PermissionGuard>
            )
          }
        ]
      },
      // JOB MANAGEMENT - Using CORRECT permission name from your database
      {
        path: 'job',
        children: [
          {
            element: (
              <PermissionGuard permission="job_management" accessType="read">
                <JobListPage permissions={{check:true}}/>
              </PermissionGuard>
            ),
            index: true
          },
          {
            path:'new',
            element: (
              <PermissionGuard permission="job_management" accessType="write">
                <JobCreatePage />
              </PermissionGuard>
            )
          },
          {
            path: ':id/edit',
            element: (
              <PermissionGuard permission="job_management" accessType="write">
                <JobEditPage />
              </PermissionGuard>
            )
          },
          {
            path: ':id',
            element: (
              <PermissionGuard permission="job_management" accessType="read">
                <JobDetailsPage />
              </PermissionGuard>
            )
          }
        ]
      },
      // PROPOSALS
      {
        path: 'proposals',
        children: [
          {
            path: 'list',
            element: (
              <PermissionGuard permission="proposals" accessType="read">
                <ProposalsPage />
              </PermissionGuard>
            )
          },
          {
            path: ':category/:id',
            element: (
              <PermissionGuard permission="proposals" accessType="read">
                <ProposalDetailsPage />
              </PermissionGuard>
            )
          }
        ]
      },
      // LOCATION
      {
        path: 'location',
        children: [
          {
            index: true,
            element: (
              <PermissionGuard permission="location_management" accessType="read">
                <LocationListPage />
              </PermissionGuard>
            )
          },
          {
            // Kept alongside the index route above for back-compat with any
            // existing /dashboard/location/list bookmarks/links.
            path: 'list',
            element: (
              <PermissionGuard permission="location_management" accessType="read">
                <LocationListPage />
              </PermissionGuard>
            )
          },
          {
            path: 'create',
            element: (
              <PermissionGuard permission="location_management" accessType="write">
                <LocationCreatePage />
              </PermissionGuard>
            )
          },
          {
            path: ':id',
            element: (
              <PermissionGuard permission="location_management" accessType="read">
                <LocationDetailsPage />
              </PermissionGuard>
            )
          },
          {
            path: ':id/edit',
            element: (
              <PermissionGuard permission="location_management" accessType="write">
                <LocationCreatePage />
              </PermissionGuard>
            )
          },
        ]
      },
      // EQUIPMENT
      {
        path: 'equipments',
        children: [
          {
            index: true,
            element: (
              <PermissionGuard permission="equipments_management" accessType="read">
                <EquipmentListPage />
              </PermissionGuard>
            )
          },
          {
            // Kept alongside the index route above for back-compat with any
            // existing /dashboard/equipments/list bookmarks/links.
            path: 'list',
            element: (
              <PermissionGuard permission="equipments_management" accessType="read">
                <EquipmentListPage />
              </PermissionGuard>
            )
          },
          {
            path: 'add',
            element: (
              <PermissionGuard permission="equipments_management" accessType="write">
                <EquipmentAddPage />
              </PermissionGuard>
            )
          },
          {
            path: ':id',
            element: (
              <PermissionGuard permission="equipments_management" accessType="read">
                <EquipmentDetailsPage />
              </PermissionGuard>
            )
          },
          {
            path: ':id/edit',
            element: (
              <PermissionGuard permission="equipments_management" accessType="write">
                <EquipmentAddPage />
              </PermissionGuard>
            )
          },
        ]
      },
      // APPLICATION MANAGEMENT - Using CORRECT permission names
      {
        path: 'application',
        children: [
          {
            path: 'received',
            element: (
              <PermissionGuard permission="received_application_management" accessType="read">
                <JobReceivedPage />
              </PermissionGuard>
            )
          },
          {
            path: 'applied',
            element: (
              <PermissionGuard permission="application_management" accessType="read">
                <JobAppliedPage />
              </PermissionGuard>
            )
          },
          {
            path: 'applied/:applicationId',
            element: (
              <PermissionGuard permission="application_management" accessType="read">
                <AppliedApplicationDetailsView />
              </PermissionGuard>
            )
          },
          {
            path: ':jobId/:candidateShortUrl',
            element: (
              <PermissionGuard permission="application_management" accessType="read">
                <ApplicationDetailsView />
              </PermissionGuard>
            )
          },
        ]
      },
      // CHAT - Using CORRECT permission name
      // Single element on parent so ChatPage stays mounted when navigating between chats
      {
        path: 'chat',
        element: (
          <PermissionGuard permission="chats" accessType="read">
            <ChatPage />
          </PermissionGuard>
        ),
        children: [
          { index: true },
          { path: ':id' }
        ]
      },
      // TALENT MANAGEMENT - Using CORRECT permission names
      {
        path: 'talent',
        children: [
          {
            element: (
              <PermissionGuard permission="talent_management" accessType="read">
                <TalentListPage permissions={{check:true}}/>
              </PermissionGuard>
            ),
            index: true
          },
          {
            path:'hired',
            element: (
              <PermissionGuard permission="talent_management" accessType="read">
                <TalentHirePage />
              </PermissionGuard>
            )
          },
          {
            path: 'availability',
            element: (
              <PermissionGuard permission="availability_management" accessType="read">
                <TalentAvailabilityPage />
              </PermissionGuard>
            )
          },
          {
            path: 'offers',
            children: [
              {
                element: (
                  <PermissionGuard permission="talent_offers" accessType="read">
                    <TalentOffersPage />
                  </PermissionGuard>
                ),
                index: true
              },
              {
                path: 'new',
                element: (
                  <PermissionGuard permission="talent_offers" accessType="write">
                    <TalentOfferNewPage />
                  </PermissionGuard>
                )
              },
            ]
          },
          {
            path: 'bookings',
            children: [
              {
                element: (
                  <PermissionGuard permission="talent_bookings" accessType="read">
                    <TalentBookingsPage />
                  </PermissionGuard>
                ),
                index: true
              },
              {
                path: ':id',
                element: (
                  <PermissionGuard permission="talent_bookings" accessType="read">
                    <TalentBookingDetailsPage />
                  </PermissionGuard>
                )
              },
              {
                path: ':id/edit',
                element: (
                  <PermissionGuard permission="talent_bookings" accessType="read">
                    <TalentBookingEditPage />
                  </PermissionGuard>
                )
              },
            ]
          },
          {
            path:'new',
            element: (
              <PermissionGuard permission="talent_management" accessType="write">
                <TalentCreatePage />
              </PermissionGuard>
            )
          },
          {
            path: ':id/edit',
            element: (
              <PermissionGuard permission="talent_management" accessType="write">
                <TalentEditPage />
              </PermissionGuard>
            )
          },
          {
            path: ':id',
            element: (
              <PermissionGuard permission="talent_management" accessType="read">
                <TalentDetailsPage />
              </PermissionGuard>
            )
          },
          {
            path: 'profile/:short_url',
            element: (
              <PermissionGuard permission="talent_management" accessType="read">
                <TalentProfilePage />
              </PermissionGuard>
            )
          },
          {
            path: 'tours',
            element: (
              <PermissionGuard permission="tours_management" accessType="read">
                <TalentToursPage />
              </PermissionGuard>
            )
          },
          {
            path: 'travel-stays',
            element: (
              <PermissionGuard permission="travel_stays_management" accessType="read">
                <TalentTravelStaysPage />
              </PermissionGuard>
            )
          },
          {
            path: 'compliances',
            element: (
              <PermissionGuard permission="compliances_management" accessType="read">
                <TalentCompliancesPage />
              </PermissionGuard>
            )
          }
        ]
      },
      {
        path: 'orders',
        children: [
          {
            element: (
              <PermissionGuard permission="orders" accessType="read">
                <OrdersListPage />
              </PermissionGuard>
            ),
            index: true
          },
          {
            path: ':id',
            element: (
              <PermissionGuard permission="orders" accessType="read">
                <OrdersDetailsPage />
              </PermissionGuard>
            )
          },
        ]
      },
      // CLIENT MANAGEMENT - Using CORRECT permission name
      {
        path: 'clients',
        children: [
          {
            element: (
              <PermissionGuard permission="client_management" accessType="read">
                <ClientListPage permissions={{check:true}}/>
              </PermissionGuard>
            ),
            index: true
          },
          {
            path:'new',
            element: (
              <PermissionGuard permission="client_management" accessType="write">
                <ClientCreatePage />
              </PermissionGuard>
            )
          },
          {
            path: ':id/edit',
            element: (
              <PermissionGuard permission="client_management" accessType="write">
                <ClientEditPage />
              </PermissionGuard>
            )
          },
          {
            path: ':id',
            element: (
              <PermissionGuard permission="client_management" accessType="read">
                <ClientDetailsPage />
              </PermissionGuard>
            )
          }
        ]
      },
      // CALENDAR - Using CORRECT permission name (if you want to protect it)
      {
        path: 'calendar',
        element: (
          <PermissionGuard permission="calendar_management" accessType="read">
            <CalendarPage />
          </PermissionGuard>
        )
      },
      // AGENT CHAT (ChatGPT-style)
      {
        path: 'agent',
        children: [
          { element: <AgentChatPage />, index: true },
          { path: ':id', element: <AgentChatPage /> },
        ],
      },
      // INVOICE MANAGEMENT - Using CORRECT permission names
      {
        path: 'invoice',
        children: [
          {
            element: (
              <PermissionGuard permission="invoice_management" accessType="read">
                <InvoiceListPage permissions={{check:true}}/>
              </PermissionGuard>
            ),
            index: true
          },
          {
            path:'new',
            element: (
              <PermissionGuard permission="invoice_management" accessType="write">
                <InvoiceCreatePage invoiceType="invoice" />
              </PermissionGuard>
            )
          },
          {
            path:'performa',
            element: (
              <PermissionGuard permission="performa_management" accessType="read">
                <PerformaInvoicePage />
              </PermissionGuard>
            )
          },
          {
            path: ':id/edit',
            element: (
              <PermissionGuard permission="invoice_management" accessType="write">
                <InvoiceEditPage />
              </PermissionGuard>
            )
          },
          {
            path: ':id',
            element: (
              <PermissionGuard permission="invoice_management" accessType="read">
                <InvoiceDetailsPage />
              </PermissionGuard>
            )
          },
          {
            path: 'performa/new',
            element: (
              <PermissionGuard permission="performa_management" accessType="write">
                <InvoiceCreatePage invoiceType="performa" />
              </PermissionGuard>
            )
          },
        ]
      },
      // EXPENSE CLAIMS REVIEW - finance/admin surface, gated on the 'expenses' permission
      {
        path: 'expenses',
        children: [
          {
            element: (
              <PermissionGuard permission="expenses" accessType="write">
                <ExpenseClaimsReviewPage />
              </PermissionGuard>
            ),
            index: true,
          },
        ],
      },
      // PROJECT MANAGEMENT - Using CORRECT permission name from your database
      {
        path: 'projects',
        children: [
          {
            element: <Navigate to="list" replace />,
            index: true
          },
          {
            path: 'list',
            element: (
              <PermissionGuard permission="project_management" accessType="read">
                <ProjectListPage permissions={{check:true}}/>
              </PermissionGuard>
            )
          },
          {
            path: 'templates',
            children: [
              {
                index: true,
                element: (
                  <PermissionGuard permission="project_management" accessType="read">
                    <ProjectTemplatesPage />
                  </PermissionGuard>
                ),
              },
              {
                path: 'new',
                element: (
                  <PermissionGuard permission="project_management" accessType="write">
                    <ProjectTemplateNewPage />
                  </PermissionGuard>
                ),
              },
              {
                path: ':id/edit',
                element: (
                  <PermissionGuard permission="project_management" accessType="write">
                    <ProjectTemplateEditPage />
                  </PermissionGuard>
                ),
              },
            ],
          },
          {
            path: 'attendence',
            element: (
              <PermissionGuard permission="project_attendence" accessType="read">
                <ProjectAttendencePage />
              </PermissionGuard>
            )
          },
          {
            path: 'members',
            element: (
              <PermissionGuard permission="project_management" accessType="read">
                <ProjectListPayrollPage />
              </PermissionGuard>
            )
          },
          {
            path: 'payoutQueue',
            element: (
              <PermissionGuard permission="project_management" accessType="read">
                <ProjectPayoutQueuePage />
              </PermissionGuard>
            )
          },
          {
            path: 'freelancerView',
            element: (
              <PermissionGuard permission="freelancer_view" accessType="read">
                <ProjectFreelancerPage />
              </PermissionGuard>
            )
          },
          {
            path: 'incentives',
            element: (
              <PermissionGuard permission="incentives" accessType="read">
                <ProjectIncentivesPage />
              </PermissionGuard>
            )
          },
          {
            path:'new',
            element: (
              <PermissionGuard permission="project_management" accessType="write">
                <ProjectCreatePage />
              </PermissionGuard>
            )
          },
          {
            path: ':id/edit',
            element: (
              <PermissionGuard permission="project_management" accessType="write">
                <ProjectEditPage />
              </PermissionGuard>
            )
          },
          {
            path: ':id/kanban',
            element: (
              <PermissionGuard permission="project_management" accessType="read">
                <ProjectKanbanPage />
              </PermissionGuard>
            )
          },
          {
            path: ':id/calendar',
            element: (
              <PermissionGuard permission="project_management" accessType="read">
                <ProjectCalendarPage />
              </PermissionGuard>
            )
          },
          {
            path: ':id/analytics',
            element: (
              <PermissionGuard permission="project_management" accessType="read">
                <ProjectAnalyticsPage />
              </PermissionGuard>
            )
          },
          {
            path: ':id/all-tasks',
            element: (
              <PermissionGuard permission="project_management" accessType="read">
                <ProjectAllTasksPage />
              </PermissionGuard>
            )
          },
          {
            path: ':id',
            element: (
              <PermissionGuard permission="project_management" accessType="read">
                <ProjectDetailsPage />
              </PermissionGuard>
            )
          },
        ]
      },
      // REPORTS
      {
        path: 'reports',
        children: [
          {
            path: 'client-revenue',
            element: (
              <PermissionGuard permission="client_revenue_report" accessType="read">
                <ClientRevenueReportPage />
              </PermissionGuard>
            )
          },
          {
            path: 'talent-earnings',
            element: (
              <PermissionGuard permission="talent_earnings_report" accessType="read">
                <TalentEarningsReportPage />
              </PermissionGuard>
            )
          },
          {
            path: 'company-earnings',
            element: (
              <PermissionGuard permission="company_earnings_report" accessType="read">
                <CompanyEarningsReportPage />
              </PermissionGuard>
            )
          },
          {
            path: 'expense-report',
            element: (
              <PermissionGuard permission="expense_report" accessType="read">
                <ExpenseReportPage />
              </PermissionGuard>
            )
          },
          {
            path: 'settlements',
            element: (
              <PermissionGuard permission="settlements" accessType="read">
                <SettlementsPage />
              </PermissionGuard>
            )
          },
          // Task 11.2 — cross-project Reports page (§3.1 N08). No
          // PermissionGuard yet: the backend route (/api/project-reports/*)
          // is gated by company-scope + verifyToken only, not checkPermission
          // — see projectReportsRoutes.js's comment.
          {
            path: 'projects',
            element: <ProjectReportsPage />,
          },
        ]
      },
      // CONTRACTS
      {
        path: 'contracts',
        children: [
          {
            element: (
              <PermissionGuard permission="contracts_management" accessType="read">
                <ContractsListPage />
              </PermissionGuard>
            ),
            index: true,
          },
          {
            path: 'templates',
            children: [
              {
                index: true,
                element: (
                  <PermissionGuard permission="contracts_management" accessType="read">
                    <ContractsTemplatesPage />
                  </PermissionGuard>
                ),
              },
              {
                path: 'new',
                element: (
                  <PermissionGuard permission="contracts_management" accessType="write">
                    <ContractTemplateNewPage />
                  </PermissionGuard>
                ),
              },
              {
                path: ':id/edit',
                element: (
                  <PermissionGuard permission="contracts_management" accessType="write">
                    <ContractTemplateEditPage />
                  </PermissionGuard>
                ),
              },
            ],
          },
          {
            path: ':id',
            element: (
              <PermissionGuard permission="contracts_management" accessType="read">
                <ContractDetailPage />
              </PermissionGuard>
            ),
          },
          {
            path: ':id/sign/:signingToken',
            element: (
              <PermissionGuard permission="contracts_management" accessType="read">
                <ContractSignDashboardPage />
              </PermissionGuard>
            ),
          },
        ],
      },
      // AUDITIONS
      {
        path: 'auditions',
        children: [
          {
            path: 'list',
            element: (
              <PermissionGuard permission="auditions_management" accessType="read">
                <AuditionsListPage />
              </PermissionGuard>
            )
          },
          {
            path: 'new',
            element: (
              <PermissionGuard permission="auditions_management" accessType="write">
                <AuditionNewPage />
              </PermissionGuard>
            )
          },
          {
            path: ':id/edit',
            element: (
              <PermissionGuard permission="auditions_management" accessType="write">
                <AuditionEditPage />
              </PermissionGuard>
            )
          },
          {
            path: ':id',
            element: (
              <PermissionGuard permission="auditions_management" accessType="read">
                <AuditionDetailPage />
              </PermissionGuard>
            )
          },
        ]
      },
      // BOOKINGS — personal "my bookings" feed (location/equipment/talent).
      // NOTE: no PermissionGuard here on purpose — there's no seeded
      // permission_name for this yet; gating on a nonexistent one locks out
      // every CRM user (see the user_management/role_management notes above).
      // Add one once the backend permissions table has a row for it.
      {
        path: 'bookings',
        element: <BookingsListPage />,
      },
      // PARTNERS
      {
        path: 'partners',
        element: (
          <PermissionGuard permission="partner_management" accessType="read">
            <PartnersListPage />
          </PermissionGuard>
        ),
      },
      // FILM MODULE — films are projects; reuse project_management permission
      {
        path: 'film',
        children: [
          {
            element: (
              <PermissionGuard permission="project_management" accessType="read">
                <FilmListPage />
              </PermissionGuard>
            ),
            index: true,
          },
          {
            path: ':projectId',
            element: (
              <PermissionGuard permission="project_management" accessType="read">
                <FilmWorkspacePage />
              </PermissionGuard>
            ),
          },
          {
            path: ':projectId/script/:scriptId',
            element: (
              <PermissionGuard permission="project_management" accessType="write">
                <FilmScriptEditorPage />
              </PermissionGuard>
            ),
          },
        ],
      },
      // NOTIFICATIONS
      {
        path: 'notifications',
        element: <NotificationsPage />,
      },
      {
        path: 'notifications/:id',
        element: <NotificationDetailPage />,
      },
      // DOCUMENTS
      {
        path: 'documents',
        element: (
          <PermissionGuard permission="documents_management" accessType="read">
            <DocumentsPage />
          </PermissionGuard>
        )
      },
      {
        path: 'documents/accept-share/:token',
        element: <AcceptSharePage />
      },
      {
        path: 'documents/accept-file-share/:token',
        element: <AcceptFileSharePage />
      },
    ],
  },
];
